import React from "react";
import {
  ClockIcon,
  UserIcon,
  UsersIcon,
  XCircleIcon,
  TrashIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  ExclamationCircleIcon,
  ClockIcon as PendingIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Button, Badge, Avatar } from "components/ui";
import { getNormalizedActivityStatus } from "constants/activityStatus.constant";
import { canDeleteFollowUp } from "permissions/permissionHelpers";


const formatTimeLocal = (timeStr) => {
  if (!timeStr) return "";
  if (/AM|PM/i.test(timeStr)) {
    return timeStr;
  }
  const match = timeStr.match(/^(\d{1,2}):(\d{2})/);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${minutes} ${ampm}`;
  }
  return timeStr;
};

export const FloatingPopoverTooltip = React.forwardRef(({
  selectedFollowUp,
  position,
  onClose,
  onViewDetails,
  onDelete,
  onMouseEnter,
  onMouseLeave
}, ref) => {
  if (!selectedFollowUp) return null;

  const channelId =
    selectedFollowUp.ChannelId ??
    selectedFollowUp.channelId ??
    selectedFollowUp.ChannelID ??
    selectedFollowUp.channelID;

  const followUpTypeId =
    selectedFollowUp.FollowUpTypeId ??
    selectedFollowUp.followUpTypeId ??
    selectedFollowUp.FollowUpTypeID ??
    selectedFollowUp.followUpTypeID;

  const activityType =
    selectedFollowUp.ActivityType ??
    selectedFollowUp.activityType;

  const isCustomActivity =
    Number(channelId) === 99 ||
    Number(followUpTypeId) === 99 ||
    Number(activityType) === 99 ||
    selectedFollowUp.isCustomActivity === true ||
    selectedFollowUp.IsCustomActivity === true;

  // Deliberately no fallback to the logged-in viewer's own name here -- when none of these
  // fields are present, the activity's assignee is genuinely unknown, and defaulting to
  // whoever happens to be viewing the calendar would misrepresent who it's actually assigned to.
  const assigneeName = selectedFollowUp.Assignee ||
                       selectedFollowUp.assignee ||
                       selectedFollowUp.AssignedTo ||
                       selectedFollowUp.assignedTo ||
                       selectedFollowUp.AssignedToName ||
                       selectedFollowUp.assignedToName ||
                       selectedFollowUp.WorkerName ||
                       selectedFollowUp.workerName ||
                       "";

  let attendeesList = selectedFollowUp.Attendee ||
                        selectedFollowUp.attendee ||
                        selectedFollowUp.Attendees ||
                        selectedFollowUp.attendees;

  if (typeof attendeesList === 'string') {
    attendeesList = attendeesList.split(',').map(name => ({ Attendee: name.trim() })).filter(a => a.Attendee);
  } else if (Array.isArray(attendeesList)) {
    attendeesList = attendeesList.map(att => {
      if (typeof att === 'string') return { Attendee: att.trim() };
      return att;
    });
  }

  return (
    <div
      ref={ref}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="animate-in fade-in zoom-in-95 fixed z-50 w-64 max-h-[calc(100vh-2rem)] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-xl duration-150 dark:border-gray-700 dark:bg-gray-800"
      style={{
        top: position.top,
        left: position.left,
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Popover Header with colored bar */}
      <div
      />

      <div className="p-3">
        {/* Header Actions (Delete & Close buttons) */}
        <div className="absolute top-2 right-2 flex items-center gap-1">
          {onDelete && canDeleteFollowUp() && selectedFollowUp._type !== "job" && (
            <button
              onClick={() => onDelete(selectedFollowUp)}
              className="rounded-full p-0.5 transition-colors hover:bg-red-50 text-red-500 hover:text-red-600 dark:hover:bg-red-900/30 cursor-pointer"
              title="Delete Activity"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded-full p-0.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
            title="Close"
          >
            <XCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
          </button>
        </div>

        {/* Title */}
        <h3 className="mb-2 pr-14 text-sm leading-tight font-semibold text-gray-900 dark:text-gray-100">
          {selectedFollowUp._type === "job"
            ? selectedFollowUp.JobName || "Job"
            : selectedFollowUp.ActivityTitle || selectedFollowUp.activityName || selectedFollowUp.Remarks || "Activity Scheduled"}
        </h3>

        {/* Activity Type Name */}
        {selectedFollowUp._type !== "job" && (selectedFollowUp.ChannelName || selectedFollowUp.channelName) && (
          <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-2">
            Type: {selectedFollowUp.ChannelName || selectedFollowUp.channelName}
          </p>
        )}

        {/* Remarks/Description */}
        {selectedFollowUp._type !== "job" &&
          selectedFollowUp.Remarks &&
          selectedFollowUp.Remarks !== (selectedFollowUp.ActivityTitle || selectedFollowUp.activityName) && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 italic">
              &quot;{selectedFollowUp.Remarks}&quot;
            </p>
          )}

        {selectedFollowUp._type === "job" && selectedFollowUp.TaskName && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">
            {selectedFollowUp.TaskName}
          </p>
        )}

        {selectedFollowUp._type !== "job" &&
          selectedFollowUp.LeadName &&
          String(selectedFollowUp.LeadName).trim().toLowerCase() !== "unknown" && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">
              Lead: {selectedFollowUp.LeadName}
            </p>
          )}

        {/* Date & Time */}
        <div className="mb-2 flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-50 dark:bg-blue-900/20">
            <CalendarDaysIcon className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <p className="text-xs leading-tight font-semibold text-gray-800 dark:text-gray-200">
              {(() => {
                const rawDate = selectedFollowUp.ScheduledDate || selectedFollowUp.scheduledDate || selectedFollowUp.DueDate || selectedFollowUp.dueDate;
                if (!rawDate) return "No Date";
                const dateObj = new Date(rawDate.split("T")[0] + "T00:00:00");
                const day = dateObj.getDate();
                const month = dateObj.getMonth() + 1;
                const year = dateObj.getFullYear();
                return `${day}/${month}/${year}`;
              })()}
            </p>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mt-0.5">
              {(() => {
                const rawTime = selectedFollowUp.ScheduledTime || selectedFollowUp.scheduledTime;
                return rawTime ? formatTimeLocal(rawTime) : (selectedFollowUp.DueDate ? formatTimeLocal(selectedFollowUp.DueDate) : "All Day");
              })()}
            </p>
          </div>
        </div>

        {/* Status */}
        {(() => {
          const { id: statusId, name: statusName } = getNormalizedActivityStatus(selectedFollowUp);
          const isJob = selectedFollowUp._type === "job";
          const lowerName = String(statusName).toLowerCase();

          let iconBg = "bg-blue-50 dark:bg-blue-900/20";
          let icon = <PendingIcon className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400" />;
          let badgeColor = "default";

          if (isJob) {
            iconBg = "bg-amber-50 dark:bg-amber-900/20";
            icon = <ClockIcon className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />;
            badgeColor = "warning";
          } else if (statusId === 2 || lowerName === "completed") {
            iconBg = "bg-green-50 dark:bg-green-900/20";
            icon = <CheckCircleIcon className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />;
            badgeColor = "success";
          } else if (statusId === 3 || lowerName === "missed") {
            iconBg = "bg-orange-50 dark:bg-orange-900/20";
            icon = <ExclamationTriangleIcon className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />;
            badgeColor = "danger";
          } else if (statusId === 4 || lowerName === "canceled" || lowerName === "cancelled") {
            iconBg = "bg-gray-50 dark:bg-gray-850";
            icon = <NoSymbolIcon className="h-3.5 w-3.5 text-gray-600 dark:text-gray-400" />;
            badgeColor = "default";
          } else if (statusId === 6 || lowerName === "overdue" || lowerName === "failed") {
            iconBg = "bg-red-50 dark:bg-red-900/20";
            icon = <ExclamationCircleIcon className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />;
            badgeColor = "danger";
          } else { // Pending (1) or ReScheduled (5)
            iconBg = "bg-yellow-50 dark:bg-yellow-900/20";
            icon = <PendingIcon className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400" />;
            badgeColor = "warning";
          }

          return (
            <div className="mb-2 flex items-center gap-2.5">
              <div className={clsx("flex h-7 w-7 items-center justify-center rounded-md", iconBg)}>
                {icon}
              </div>
              <div>
                <p className="text-[11px] leading-tight text-gray-500 dark:text-gray-400 mb-0.5">
                  Status
                </p>
                <Badge
                  variant="soft"
                  color={badgeColor}
                  className="text-[10px] font-bold px-1.5 py-0.5 tracking-wide leading-none select-none"
                >
                  {statusName}
                </Badge>
              </div>
            </div>
          );
        })()}

        {/* Assignee — plain text */}
        {assigneeName && (
          <div className="mb-2 flex items-center gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-purple-50 dark:bg-purple-900/20 shrink-0">
              <UserIcon className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] leading-tight text-gray-500 dark:text-gray-400 mb-0.5">
                Assignee
              </p>
              <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                {assigneeName}
              </p>
            </div>
          </div>
        )}

        {/* Attendees — avatars only */}
        {Array.isArray(attendeesList) && attendeesList.length > 0 && (
          <div className="mb-2 flex items-start gap-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-indigo-50 dark:bg-indigo-900/20 mt-0.5 shrink-0">
              <UsersIcon className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] leading-tight text-gray-500 dark:text-gray-400">
                Attendees
              </p>
              <div className="flex -space-x-1.5 overflow-visible pl-1 pt-1.5 pb-1">
                {attendeesList.map((att, idx) => {
                  const attName = att.Attendee || att.attendee || att.Name || att.name || att.FullName || att.Fullname || att.fullName || att.fullname;
                  if (!attName) return null;
                  return (
                    <div key={idx} className="relative group/avatar" title={attName}>
                      <Avatar
                        size={7}
                        name={attName}
                        initialColor="auto"
                        className="shadow-none border-2 border-white dark:border-gray-800"
                        classNames={{
                          root: "origin-bottom transition-transform hover:z-10 hover:scale-125",
                          display: "text-[9px] font-bold"
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* View Details Action */}
        <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 flex justify-end">
          <Button
            size="sm"
            variant="filled"
            color="primary"
            className="w-full text-xs font-bold py-1.5"
            onClick={() => onViewDetails(selectedFollowUp)}
          >
            View Details
          </Button>
        </div>
      </div>
    </div>
  );
});

FloatingPopoverTooltip.displayName = "FloatingPopoverTooltip";
